<script setup lang="ts">
import type { DtwinMetricsCounts } from "~/modules/dashboard/models/DtwinMetricsCounts.type";
import { RoundMode } from "~/modules/dtwin/utils/dtwinsListConnectivityStatus";
import { getDuration } from "~/modules/dtwin/utils/getDuration";

const props = defineProps<{
  /**
   * Metrics count by duration
   * Must match the following format:
   * {
   *   "<XXX": n1,
   *   ">=XXX": n2,
   *   ">=YYY": n3,
   *   ">=ZZZ": n4,
   *   ...,
   *   "UNKNOWN": n
   * }
   */
  dtwinMetricsCounts: DtwinMetricsCounts;
  dtwinModelFriendlyName: string;
}>();

const { t } = useI18n();

const dtwinMetricsCountsFormatted: ComputedRef<DtwinMetricsCounts> = computed(
  () => {
    const dtwinMetricsCountsFormatted: DtwinMetricsCounts = {};

    for (const [key, value] of Object.entries(props.dtwinMetricsCounts).sort(
      (entry1, entry2) =>
        // ascending sort by duration
        entry1[0].localeCompare(entry2[0], undefined, { numeric: true }),
    )) {
      if (key.startsWith("<")) {
        const duration = getDuration(
          parseInt(key.replace("<", "")),
          RoundMode.ROUND_UP,
          t,
        );

        const formattedKey = t("dashboard.connectivity.states.lessThan", {
          duration,
        });

        dtwinMetricsCountsFormatted[formattedKey] = value;
      } else if (key.startsWith(">=")) {
        const duration = getDuration(
          parseInt(key.replace(">=", "")),
          RoundMode.ROUND_UP,
          t,
        );

        const formattedKey = t("dashboard.connectivity.states.moreThan", {
          duration,
        });

        dtwinMetricsCountsFormatted[formattedKey] = value;
      } else {
        const formattedKey = t("dashboard.connectivity.states.unknown");

        dtwinMetricsCountsFormatted[formattedKey] =
          (dtwinMetricsCountsFormatted[formattedKey] || 0) + value;
      }
    }

    return dtwinMetricsCountsFormatted;
  },
);
</script>

<template>
  <dashboard-dtwin-doughnut-chart
    :dtwin-metrics-counts="dtwinMetricsCountsFormatted"
    :dtwin-model-friendly-name="dtwinModelFriendlyName"
    :background-colors="['#039855', '#F18006', '#B54708', '#D1D1D6']"
  />
</template>
